import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Eye, Tag, Phone } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { publicAPI } from '../services/api';
import { companyInfo } from '../data/mock';

// Markdown component overrides - defined outside to avoid re-creation on each render
const MarkdownH2 = ({ node, ...props }) => (
  <h2 className="text-2xl font-bold text-[#007897] mt-8 mb-4 pb-2 border-b border-gray-200" {...props} />
);

const MarkdownH3 = ({ node, ...props }) => (
  <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3" {...props} />
);

const MarkdownP = ({ node, ...props }) => (
  <p className="text-gray-600 leading-relaxed mb-4" {...props} />
);

const MarkdownUl = ({ node, ...props }) => (
  <ul className="list-disc pl-6 my-4 space-y-2" {...props} />
);

const MarkdownOl = ({ node, ...props }) => (
  <ol className="list-decimal pl-6 my-4 space-y-2" {...props} />
);

const MarkdownLi = ({ node, ...props }) => (
  <li className="text-gray-600" {...props} />
);

const MarkdownStrong = ({ node, ...props }) => (
  <strong className="font-semibold text-gray-800" {...props} />
);

const MarkdownTable = ({ node, ...props }) => (
  <div className="overflow-x-auto my-6">
    <table className="min-w-full border-collapse border border-gray-200" {...props} />
  </div>
);

const MarkdownThead = ({ node, ...props }) => (
  <thead className="bg-[#007897]" {...props} />
);

const MarkdownTbody = ({ node, ...props }) => (
  <tbody {...props} />
);

const MarkdownTr = ({ node, ...props }) => (
  <tr className="border-b border-gray-200" {...props} />
);

const MarkdownTh = ({ node, ...props }) => (
  <th className="px-4 py-3 text-left text-white font-semibold" {...props} />
);

const MarkdownTd = ({ node, ...props }) => (
  <td className="px-4 py-3 text-gray-600 border border-gray-200" {...props} />
);

// Static components object for ReactMarkdown
const markdownComponents = {
  h2: MarkdownH2,
  h3: MarkdownH3,
  p: MarkdownP,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  strong: MarkdownStrong,
  table: MarkdownTable,
  thead: MarkdownThead,
  tbody: MarkdownTbody,
  tr: MarkdownTr,
  th: MarkdownTh,
  td: MarkdownTd,
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await publicAPI.getBlogBySlug(slug);
        setBlog(response.data);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
            <p className="text-gray-600 mb-8">The article you are looking for does not exist or has been removed.</p>
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 bg-[#007897] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#006680] transition-colors"
            >
              <ArrowLeft size={18} /> Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-8 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            aria-label="Back to Blogs"
          >
            <ArrowLeft size={18} />
          </Link>
          
          <span className="inline-block bg-white/20 text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
            {blog.category}
          </span>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {blog.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-3">
            {blog.author.includes('&') ? (
              blog.author.split('&').map((author, idx) => (
                <span key={idx} className="flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full font-semibold">
                  <User size={16} />
                  {author.trim()}
                </span>
              ))
            ) : (
              <span className="flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full font-semibold">
                <User size={16} />
                {blog.author}
              </span>
            )}
            <span className="flex items-center gap-2 text-white/80">
              <Eye size={16} />
              {blog.views} views
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {blog.image && (
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-64 md:h-96 object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=1200';
                }}
              />
            )}
            
            <div className="p-8 md:p-12">
              {/* Content - rendered as markdown with styled headings */}
              <div className="prose prose-lg max-w-none 
                prose-h2:text-2xl prose-h2:font-bold prose-h2:text-[#007897] prose-h2:mt-8 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
                prose-h3:text-xl prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-6 prose-h3:mb-3
                prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                prose-li:my-1 prose-li:text-gray-600
                prose-table:border-collapse prose-table:w-full prose-table:my-4
                prose-th:bg-[#007897] prose-th:text-white prose-th:p-3 prose-th:text-left
                prose-td:border prose-td:border-gray-200 prose-td:p-3
                prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-gray-800 prose-strong:font-semibold">
                {/* Check for actual HTML tags, not just < character */}
                {blog.content.match(/<[a-z][\s\S]*>/i) ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content) }} />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {blog.content}
                  </ReactMarkdown>
                )}
              </div>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag size={18} className="text-gray-500" />
                    {blog.tags.map((tag, index) => (
                      <span key={`item-${index}`} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 bg-gradient-to-r from-[#007897] to-[#005a73] rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-2">Have Questions About Your Health?</h3>
            <p className="text-white/80 mb-6">Our team of experts is here to help you with all your diagnostic needs.</p>
            <a
              href={`tel:${companyInfo.phones[0]}`}
              className="inline-flex items-center gap-2 bg-white text-[#007897] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Phone size={18} />
              Contact Us: {companyInfo.phones[0]}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
